import { cn } from "@istari-frontend/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@istari-frontend/ui";

import { ResourceType, UserGrantType } from "~/enums";
import { accessOptions } from "~/permissions";
import { roleLabel } from "~/permissions/role-label";

type PermissionSelectProps = {
  currentUserPermission: UserGrantType | string;
  selectedPermission: UserGrantType | string | undefined;
  onPermissionChange: (permission: UserGrantType) => void;
  disabled?: boolean;
  userId?: string;
  resourceType?: ResourceType;
  variant?: "ghost" | "default";
  className?: string;
  itemClassName?: string;
  labelVariant?: "permission" | "role";
  ariaLabel?: string;
};

const SYSTEM_PERMISSION_GRANTS: UserGrantType[] = [
  UserGrantType.viewer,
  UserGrantType.editor,
  UserGrantType.administrator,
];

const RESOURCE_PERMISSION_GRANTS: UserGrantType[] = [
  UserGrantType.viewer,
  UserGrantType.editor,
  UserGrantType.administrator,
];

const EDITOR_SYSTEM_PERMISSION_GRANTS: UserGrantType[] = [UserGrantType.viewer, UserGrantType.editor];

const EDITOR_RESOURCE_PERMISSION_GRANTS: UserGrantType[] = [UserGrantType.viewer, UserGrantType.editor];

const getPermissionOptionsFromGrants = (grants: UserGrantType[], labelVariant: "permission" | "role") => {
  const allowedGrants = new Set(grants);
  return accessOptions
    .filter((permission) => allowedGrants.has(permission.value as UserGrantType))
    .map((option) => ({
      ...option,
      label: labelVariant === "role" ? roleLabel(option.value as UserGrantType) : option.label,
    }));
};

const getAccessBasedOnRole = (
  access: string,
  resourceType?: ResourceType,
  labelVariant: "permission" | "role" = "permission",
) => {
  const isSystemResource = resourceType === ResourceType.System;
  if (access === UserGrantType.upstreamRemoteOwner) {
    return [
      {
        label: labelVariant === "role" ? roleLabel(UserGrantType.viewer) : "Can view",
        value: UserGrantType.viewer,
      },
    ];
  }

  if (access === UserGrantType.editor || access === UserGrantType.upstreamRemoteEditOwner) {
    return getPermissionOptionsFromGrants(
      isSystemResource ? EDITOR_SYSTEM_PERMISSION_GRANTS : EDITOR_RESOURCE_PERMISSION_GRANTS,
      labelVariant,
    );
  }

  return getPermissionOptionsFromGrants(
    isSystemResource ? SYSTEM_PERMISSION_GRANTS : RESOURCE_PERMISSION_GRANTS,
    labelVariant,
  );
};

export function PermissionSelect({
  currentUserPermission,
  selectedPermission,
  onPermissionChange,
  disabled = false,
  userId,
  resourceType,
  variant = "ghost",
  className,
  itemClassName,
  labelVariant = "permission",
  ariaLabel = "Permission",
}: PermissionSelectProps) {
  const permissionOptions = getAccessBasedOnRole(currentUserPermission, resourceType, labelVariant);

  return (
    <Select
      value={selectedPermission}
      onValueChange={(val) => onPermissionChange(val as UserGrantType)}
      disabled={disabled}
    >
      <SelectTrigger
        data-testid={userId ? `permission-select-${userId}` : "permission-select"}
        variant={variant}
        size="sm"
        className={cn("h-6 w-fit text-xs", className)}
        aria-label={ariaLabel}
      >
        <SelectValue placeholder="Select..." />
      </SelectTrigger>
      <SelectContent>
        {permissionOptions.map((option) => (
          <SelectItem key={option.value} value={option.value} className={cn("text-xs", itemClassName)}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
